const SESSION_KEY_PREFIX = "foundit:tab-session";

export type TabSessionEnvelope<T> = {
  version: number;
  updatedAt: number;
  data: T;
};

const isBrowser = (): boolean =>
  typeof window !== "undefined" && window.sessionStorage !== undefined;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const getTabSessionKey = (namespace: string): string =>
  `${SESSION_KEY_PREFIX}:${namespace}`;

const parseEnvelope = <T>(raw: string | null): TabSessionEnvelope<T> | null => {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as TabSessionEnvelope<T>;
    if (
      !parsed ||
      typeof parsed !== "object" ||
      typeof parsed.version !== "number" ||
      parsed.data === undefined
    ) {
      return null;
    }
    return parsed;
  } catch (error) {
    console.error("Failed to parse tab session value", error);
    return null;
  }
};

export const getTabSession = <T>(
  namespace: string,
  expectedVersion?: number,
): T | null => {
  if (!isBrowser() || !namespace) return null;

  try {
    const envelope = parseEnvelope<T>(
      window.sessionStorage.getItem(getTabSessionKey(namespace)),
    );
    if (!envelope) return null;
    if (
      typeof expectedVersion === "number" &&
      envelope.version !== expectedVersion
    ) {
      return null;
    }
    return envelope.data;
  } catch (error) {
    console.error("Failed to read tab session", error);
    return null;
  }
};

export const setTabSession = <T>(
  namespace: string,
  data: T,
  version: number = 1,
): boolean => {
  if (!isBrowser() || !namespace || data === undefined) return false;

  try {
    const envelope: TabSessionEnvelope<T> = {
      version,
      updatedAt: Date.now(),
      data,
    };
    window.sessionStorage.setItem(
      getTabSessionKey(namespace),
      JSON.stringify(envelope),
    );
    return true;
  } catch (error) {
    console.error("Failed to write tab session", error);
    return false;
  }
};

export const updateTabSession = <T extends Record<string, unknown>>(
  namespace: string,
  partial: Partial<T>,
  version: number = 1,
): boolean => {
  if (!isBrowser() || !namespace || !isPlainObject(partial)) return false;

  const current = getTabSession<T>(namespace, version);
  const nextData = {
    ...(current ?? ({} as T)),
    ...partial,
  } as T;

  return setTabSession(namespace, nextData, version);
};

export const removeTabSession = (namespace: string): void => {
  if (!isBrowser() || !namespace) return;

  try {
    window.sessionStorage.removeItem(getTabSessionKey(namespace));
  } catch (error) {
    console.error("Failed to remove tab session", error);
  }
};

export const hasTabSession = (
  namespace: string,
  expectedVersion?: number,
): boolean => getTabSession(namespace, expectedVersion) !== null;
